<template>
    <div class="row input-container">
        <div class="col-12">
            <div class="input-wrapper">
                <input
                    :class="['text-input', value && 'filled']"
                    :name="name"
                    :id="name"
                    :maxlength="maxLength"
                    @change="handleChange"
                    @keydown.enter.prevent.stop="search"
                    @input="searchType === 'predictive' && predictiveSearch()"
                    type="text"
                    v-bind="{ disabled: inputDisabled }"
                    v-model="value"
                    ref="inputSearchField"
                    @blur="
                        () => {
                            resetValue()
                            triggerValidate()
                        }
                    "
                    @click="handleElementClick(name, 'input')"
                />

                <FloatingLabel :label="label" :icon="icon" />
                <InputIcon
                    v-if="searchType === 'predictive'"
                    v-show="searchingSpinner"
                    icon="fa fa-spinner fa-pulse fa-lg fa-fw"
                    key="spinner"
                />

                <div
                    v-if="searchType === 'standard'"
                    class="button search-button wizard-button"
                    :class="{ searching: inputDisabled, disabled: fieldEmpty }"
                    @click="search"
                >
                    <span v-show="!searching">
                        <i class="far fa-search me-2"></i>
                    </span>

                    <div class="spinner-border spinner-border-sm text-light me-2" role="status" v-show="searching">
                        <span class="sr-only">{{ $t('form.general.loading') }}...</span>
                    </div>

                    <span class="find-postcode">{{ $t('form.general.find') }}</span>
                </div>

                <div class="error" v-if="displayErrorMessage">{{ displayErrorMessage }}</div>
            </div>
        </div>

        <div class="col-12 options-wrapper" v-if="!manualInput && options.length">
            <div class="row">
                <div
                    class="col-12 option"
                    :class="{
                        'last-option': options.length - 1 == optionIndex || options.length == 1,
                        selected: selectedOption && selectedOption.Id == option.Id,
                    }"
                    v-for="(option, optionIndex) in options"
                    :key="optionIndex"
                    @click="searchSelect(option)"
                >
                    {{ searchResultsDes(option) }}
                </div>
            </div>
        </div>

        <MessageButton class="mt-3" v-if="showOverideMessage" @click="showManualFields(true)">
            {{ manualMessage }}
        </MessageButton>

        <MessageButton class="mt-3" v-if="showEditMessage" @click="showManualFields(true, true)">
            {{ editMessage }}
        </MessageButton>
    </div>
</template>

<script>
import { useStore } from 'vuex'
import { useField } from 'vee-validate'
import { titleCase } from '@js/utilities/language.js'
import { networkErrorMessage } from '@js/utilities/error.js'
import { Mask } from 'maska'

// lodash components
import debounce from 'lodash/debounce'
import _get from 'lodash/get'
import startCase from 'lodash/startCase'

export default {
    props: {
        disabled: Boolean,
        editMessage: String,
        customErrorMessage: String,
        icon: String,
        label: String,
        manualInput: Boolean,
        manualMessage: String,
        name: String,
        required: Boolean,
        searchType: String,
        searchUrl: String,
        postMethod: Boolean,
        selectedName: String,
        validationLabel: String,
        validationRules: {
            default: () => {},
        },
        vuexKey: String,
    },
    data() {
        return {
            options: [],
            searching: false,
            networkError: null,
            searchingSpinner: false,
        }
    },
    setup(props) {
        const store = useStore()
        const { errorMessage, handleChange, resetField, validate } = useField(props.name, props.validationRules, {
            label: titleCase(props.name),
        })

        return {
            errorMessage,
            handleChange,
            resetField,
            store,
            validate,
        }
    },
    mounted() {
        if (this.selectedOption && !this.options.length) {
            this.options.push(this.selectedOption)
        }

        setTimeout(() => {
            this.resetField({
                value: this.value,
            })
        }, 10)
    },
    computed: {
        mask() {
            return this.store.getters.field(this.name, this.vuexKey || 'data', !!this.vuexKey)?.mask
        },
        displayName: function () {
            return startCase(this.name)
        },
        maxLength() {
            if (this.validationRules && this.validationRules.max) {
                return this.validationRules.max
            } else if (this.validationRules && this.validationRules.digits) {
                return this.validationRules.digits
            }
            return null
        },
        value: {
            get() {
                const getValue = this.store.getters.fieldValue(this.name, this.vuexKey || 'data', !!this.vuexKey)

                if (this.mask) {
                    const maskValue = new Mask({ mask: this.mask })
                    return maskValue.unmasked(getValue)
                }

                return getValue
            },
            set(value) {
                if (this.vuexKey) {
                    this.store.commit('setDataFieldValue', {
                        name: this.name,
                        value: value,
                        key: this.vuexKey,
                    })
                } else {
                    this.store.dispatch('setFieldValue', {
                        name: this.name,
                        value: value,
                    })
                }

                this.resetField({
                    value: value,
                })

                this.$emit('input', {
                    name: this.name,
                    value: value,
                })
            },
        },
        selectedOption() {
            const selectedOption = this.store.state.apply.currentRouteStep.selected
            return selectedOption && selectedOption.Text ? selectedOption : null
        },
        inputDisabled: function () {
            return this.disabled || this.searching ? true : false
        },
        fieldEmpty: function () {
            return this.value ? false : true
        },
        showOverideMessage: function () {
            return this.manualMessage &&
                !this.manualInput &&
                !this.searching &&
                this.options.length &&
                !this.selectedOption
                ? true
                : false
        },
        showEditMessage: function () {
            return this.editMessage && !this.manualInput && !this.searching && this.selectedOption ? true : false
        },
        serverError() {
            return this.store.getters.fieldServerError(this.name)
        },
        displayErrorMessage() {
            return (
                this.networkError ||
                (this.errorMessage ? this.customErrorMessage || this.errorMessage : this.serverError)
            )
        },
    },
    watch: {
        selectedOption: function (value) {
            if (!value) {
                this.options = []
            }
        },
    },
    methods: {
        predictiveSearch: debounce(function () {
            this.search()
        }, 500),
        showManualFields: function (manual, keepValues) {
            this.emitter.emit('searchField:showManualFields', {
                manual,
                keepValues,
            })
        },
        searchSelect: function (selectedValue) {
            if (selectedValue.Type == 'Container') {
                this.search(selectedValue.Id)
                return
            }

            this.emitter.emit('searchField:selected', {
                name: this.name,
                value: selectedValue,
            })
        },
        search: async function (containerId = '') {
            if (this.fieldEmpty) {
                this.options = []
                return
            }

            if (this.searchType !== 'predictive') {
                this.searching = true
            }

            this.options = []
            let response = null
            this.networkError = null

            try {
                this.searchingSpinner = true

                if (this.postMethod) {
                    const data = {
                        postcode: this.value,
                        container_id: containerId,
                    }

                    response = await axios.post(this.searchUrl, data)
                } else {
                    response = await axios.get(this.searchUrl + encodeURIComponent(this.value))
                }

                if (response && response.data) {
                    this.options = response.data.Items

                    if (!this.options.length) {
                        this.showManualFields(true, false)
                    }
                }

                this.searchingSpinner = false
            } catch (error) {
                this.searchingSpinner = false
                this.networkError = networkErrorMessage(this.store, error)
                console.error(error)
            }

            this.searching = false
        },
        focus() {
            this.autoFocus(this.$refs.inputSearchField)
        },
        resetValue: debounce(function () {
            if (this.options.length === 0 && this.selectedOption !== this.value && this.value === '') {
                if (this.vuexKey) {
                    this.store.commit('setDataFieldValue', {
                        name: this.name,
                        value: null,
                        key: this.vuexKey,
                    })
                } else {
                    this.store.dispatch('setFieldValue', {
                        name: this.name,
                        value: null,
                    })
                }
            }
        }, 250),
        triggerValidate: function () {
            this.$nextTick(async () => {
                this.valid = await this.validate()
            })
        },
        searchResultsDes(option) {
            return [option.Text.trim(), option.Description.trim()]
                .filter(Boolean)
                .join(option.Type == 'Container' ? ' ' : ', ')
        },
        handleElementClick(elementName, elementType) {
            this.emitter.emit('elementClicked', { elementName, elementType })
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';

.input-wrapper {
    position: relative;
    margin-bottom: 20px;

    .search-button {
        background-color: var(--wizard-button-color);
        color: var(--wizard-button-text-color);
        cursor: pointer;
        font-size: rem(16px);
        font-weight: bold;
        height: 47px;
        line-height: 47px;
        max-width: 110px;
        padding-left: 12px;
        padding-right: 12px;
        position: absolute;
        right: 3px;
        text-align: center;
        top: 3px;
        user-select: none;
        z-index: 100;

        &.searching {
            opacity: 0.5;
            cursor: default;
        }

        &.disabled {
            opacity: 0.5;
            cursor: default;

            &:hover {
                background-color: var(--wizard-button-color);
            }
        }
    }
}
.options-wrapper {
    border-radius: 4px;
    border: 1px solid #e0e1e5;
    text-align: left;
    max-height: 344px;
    overflow: auto;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE 10+ */
    padding-bottom: 15px;

    &::-webkit-scrollbar {
        display: none; /* Chrome Safari */
    }

    .option {
        cursor: pointer;
        font-weight: normal;
        color: #242a2f;
        font-size: rem(16px);
        padding: 15px 15px 0 15px;

        &.selected {
            background-color: #e0e1e5;
        }

        &:after {
            content: '';
            display: block;
            margin: 15px auto 0 auto;
            width: 100%;
            border-bottom: 1px solid #e0e1e5;
        }

        &.last-option {
            border: none;
            &:after {
                display: none;
            }
        }
    }
}

.text-input-icon {
    left: 90%;
    position: absolute;
    top: 15px;
    transition: 0.2s ease all;
    overflow: hidden;

    @media screen and (max-width: $bp-large) {
        left: 85%;
    }

    @media screen and (max-width: 16em) {
        left: 80%;
    }

    .icon {
        color: black;
        font-size: 20px;
        vertical-align: middle;
    }
}
</style>
